/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  ClickAwayListener,
  Grow,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { makeStyles } from '@mui/styles';
import moment from 'moment';

const useStyles = makeStyles(() => ({

  container: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  textField: {
    fill: 'white',
    margin: 0,
    color: 'white !important',
    '& label.Mui-focused': {
      color: 'white',
      borderColor: 'white',
    },

    '& .MuiButtonBase-root': {
      color: 'white',
      fill: 'white',
    },

    '& input ': {
      color: 'white',
    },
    '& label': {
      color: 'white',
    },
    '& .MuiInput-underline:after': {
      color: 'white',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.23)',
        color: 'white',
      },
      '&:hover fieldset': {
        borderColor: 'white',
        color: 'white',
      },
      '&.Mui-focused fieldset': {
        color: 'white',
        borderColor: 'white',
      },
    }
  },

  cssOutlinedInput: {
    color: 'white',
  },

  cssFocused: {
    color: 'white',
  },

}));

const SearchTextField = ({
  field, value, comparator, handleChange, handleChangeDate, handleChangeText, handleSelect,
  setDefaultComparator
}) => {
  const classes = useStyles();

  const [openComparatorMenu, setOpenComparatorMenu] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);

  const [openDate, setOpenDate] = useState(null);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    setDefaultComparator(field.comparators[0]);
  }, [setDefaultComparator, field]);

  // anchorRef sets the anchor for the comparator menu
  const anchorRef = useRef(null);

  // textInput sets the ref for input focus
  const textInput = useRef(null);

  useEffect(() => {
    if (focus && textInput.current) {
      /*
        This is one of those rare cases that a setTimeout is the
        solution. After extensive research it turns out that
        setTimeout is a common solution for setting focus with
        material UI. In this case we could have updated a local
        state value for dropdown however, that did not work for the
        date component so it made most sense to keep setTimeout for
        both vs solving for each individually
      */
      setTimeout(() => {
        textInput.current.focus();
      }, 100);
    }
  }, [value, focus]);

  const handleToggle = () => {
    if (!comparator) {
      setDefaultComparator(field.comparators[0]);
    }
    if (anchorRef.current) {
      anchorRef.current.focus();
    }
    setOpenComparatorMenu((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenComparatorMenu(false);
  };

  // once click into menu list, if user clicks tab > close menu
  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpenComparatorMenu(false);
    }
  }

  const textField = () => {
    const isRange = comparator === 'between';
    const parts = value ? value.split('~') : [];
    const text1 = parts.length > 0 ? parts[0] : '';
    const text2 = isRange && parts.length > 1 ? parts[1] : '';

    return (
      <Box
        ref={anchorRef}
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TextField
          inputRef={textInput}
          aria-controls={openComparatorMenu ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          margin="dense"
          className={classes.textField}
          fullWidth
          color="secondary"
          name={field.name}
          label={field.name}
          type={field.type === 'Integer' ? 'number' : 'text'}
          value={text1}
          onChange={(e) => {
            setDefaultComparator(field.comparator);
            handleChangeText(field.name, e.target.value, text2);
          }}
          size="small"
          variant="outlined"
          autoComplete="off"
          InputProps={{
            classes: {
              root: classes.cssOutlinedInput,
              focused: classes.cssFocused,
            },
            startAdornment: comparator && (value || openComparatorMenu) ? (
              <InputAdornment style={{ color: 'white' }} position="start">
                {comparator}
                :
              </InputAdornment>
            ) : '',
          }}
        />
        {isRange ? (
          <TextField
            inputRef={textInput}
            aria-controls={openComparatorMenu ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
            margin="dense"
            className={classes.textField}
            fullWidth
            color="secondary"
            name={field.name}
            label={field.name}
            type={field.type === 'Integer' ? 'number' : 'text'}
            value={text2}
            onChange={(e) => {
              setDefaultComparator(field.comparator);
              handleChangeText(field.name, text1, e.target.value);
            }}
            size="small"
            variant="outlined"
            autoComplete="off"
            InputProps={{
              classes: {
                root: classes.cssOutlinedInput,
                focused: classes.cssFocused,
              },
            }}
          />
        ) : ''}
      </Box>
    );
  };

  const dateField = () => {
    const isRange = comparator === 'between';
    const open1 = field.name;
    const open2 = `${open1}_2`;
    const parts = value ? value.split('~') : [];
    const date1 = parts.length > 0 ? moment(parts[0]) : null;
    const date2 = isRange && parts.length > 1 ? moment(parts[1]) : null;

    return (
      <Box
        ref={anchorRef}
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <DatePicker
          ref={textInput}
          autoOk
          open={openDate === open1}
          onOpen={() => setOpenDate(open1)}
          onClose={() => setOpenDate(null)}
          autoComplete="off"
          name={field.name}
          label={field.name}
          value={date1}
          onChange={(date) => {
            setFocus(true);
            handleChangeDate(field.name, date, date2);
          }}
          aria-controls={openComparatorMenu ? 'menu-list-grow' : undefined}
          format="MM/DD/YYYY"
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              margin: 'dense',
              size: 'small',
              className: classes.textField,
              inputRef: textInput,
              onClick: () => {
                setOpenDate(() => null);
                handleToggle();
              },
              InputProps: {
                classes: {
                  root: classes.cssOutlinedInput,
                  focused: classes.cssFocused
                },
                startAdornment: comparator && !isRange && (value || openComparatorMenu || openDate || comparator.startsWith('previous'))
                  ? (
                    <InputAdornment style={{ color: 'white' }} position="start">
                      {comparator}
                      :
                    </InputAdornment>
                  ) : '',
                endAdornment: (
                  <InputAdornment style={{ color: 'white' }} position="end">
                    <IconButton
                      aria-label="Pick Date"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDefaultComparator(field.comparators[0]);
                        setOpenComparatorMenu(() => false);
                        setOpenDate(openDate === null ? open1 : null);
                      }}
                      edge="end"
                      size="small"
                    >
                      <CalendarTodayIcon />
                    </IconButton>
                  </InputAdornment>
                )
              },
              helperText: null
            }
          }}
        />
        {isRange && (date1 || date2 || openComparatorMenu || openDate) ? (
          <>
            &nbsp;~&nbsp;
            <DatePicker
              ref={textInput}
              autoOk
              open={openDate === open2}
              onOpen={() => setOpenDate(open2)}
              onClose={() => setOpenDate(null)}
              autoComplete="off"
              name={`${field.name}End`}
              label={field.name}
              value={date2}
              onChange={(date) => {
                setFocus(true);
                handleChangeDate(field.name, date1, date);
              }}
              format="MM/DD/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  margin: 'dense',
                  size: 'small',
                  className: classes.textField,
                  inputRef: textInput,
                  onClick: () => {
                    setOpenDate(() => null);
                    setOpenComparatorMenu(() => false);
                  },
                  InputProps: {
                    classes: {
                      root: classes.cssOutlinedInput,
                      focused: classes.cssFocused
                    },
                    endAdornment: (
                      <InputAdornment style={{ color: 'white' }} position="end">
                        <IconButton
                          aria-label="Pick End Date"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenComparatorMenu(() => false);
                            setOpenDate(openDate === null ? open2 : null);
                          }}
                          edge="end"
                          size="small"
                        >
                          <CalendarTodayIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  },
                  helperText: null
                }
              }}
            />
          </>
        ) : ''}
      </Box>
    );
  };

  const dropDownfield = () => (
    <>
      <TextField
        inputRef={textInput}
        ref={anchorRef}
        aria-controls={openComparatorMenu ? 'simple-menu' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        margin="dense"
        className={classes.textField}
        fullWidth
        color="secondary"
        name={field.name}
        label={field.name}
        value={value}
        size="small"
        variant="outlined"
        autoComplete="off"
        InputProps={{
          classes: {
            root: classes.cssOutlinedInput,
            focused: classes.cssFocused
          },
          startAdornment:
            comparator && (value || openComparatorMenu) ? (
              <InputAdornment style={{ color: 'white' }} position="start">
                {comparator}
                :
              </InputAdornment>
            ) : (
              ''
            ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="View Options"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenComparatorMenu(false);
                  setOpenMenu(true);
                }}
                size="large"
              >
                {!openMenu ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      {/*
        Similar to the note above - in most situations the on click event will trigger handleToggle
        which will set the default comparator for the field, the handleChange event in the parent
        component needs to use the defaultComparator in order to set it on the parent state.
        BUT since dropdowns need to tigger the handleChange event on item select the default
        comparator hasnt been set and it is undefined in the parent handleChange function, so
        instead we set it when we enter the menu popper
      */}
      <Menu
        id="simple-menu"
        anchorEl={anchorRef.current}
        keepMounted
        open={openMenu}
        onClose={() => {
          setOpenMenu(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionProps={{
          onEnter: () => setDefaultComparator(field.comparators[0])
        }}
      >
        <MenuItem
          onClick={() => {
            handleChange(field.name, '');
            setFocus(true);
            setOpenMenu(false);
          }}
        >
          <i>Clear Selection</i>
        </MenuItem>
        {field && field.pickList
          ? field.pickList.map((item) => (
            <MenuItem
              key={item}
              onClick={(e) => {
                e.stopPropagation();
                setFocus(true);
                handleChange(field.name, item);
                setOpenMenu(false);
              }}
            >
              {item}
            </MenuItem>
          ))
          : ''}
      </Menu>
    </>
  );

  const fieldBuilder = () => {
    if (field.pickList) {
      return dropDownfield();
    }
    switch (field.type) {
      case 'Enum': return dropDownfield();
      case 'Integer': return textField();
      case 'DateTime': return dateField();
      case 'Date': return dateField();
      default: return textField();
    }
  };

  const parts = value ? value.split('~') : [];
  const value1 = parts.length > 0 ? parts[0] : null;

  return (
    <>
      <Popper
        style={{ zIndex: 999 }}
        open={openComparatorMenu}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={false}
                  id="menu-list-grow"
                  onKeyDown={() => handleListKeyDown}
                >
                  {
                    field.comparators.map((comparatorKey) => (
                      <MenuItem
                        key={comparatorKey}
                        onClick={() => {
                          if (textInput.current) {
                            textInput.current.focus();
                          }
                          handleSelect(field.name, comparatorKey);
                        }}
                      >
                        <b>{comparatorKey}</b>
                        :
                        <Typography style={{ color: '#263238', opacity: '0.4', paddingLeft: 4 }}>
                          {
                            field.type === 'Date' && value && comparatorKey === 'between' ? value : value1
                          }
                        </Typography>
                      </MenuItem>
                    ))
                  }
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {
        fieldBuilder()
      }
    </>
  );
};

export default SearchTextField;
